import { Delete as DeleteIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateCategoryDialog from "src/components/news/CreateCategoryDialog";
import { del, get, post } from "src/utils/networkUtils";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

type NewsCategory = {
	categoryId: number;
	name: string;
};

export default function NewsCategoriesListPage() {
	const navigate = useNavigate();
	const [rows, setRows] = useState<NewsCategory[]>([]);
	// const [searchParams, setSearchParams] = useSearchParams();

	const [openCreateCategoryDialog, setOpenCreateCategoryDialog] = useState(false);
	const [categoryName, setCategoryName] = useState("");
	const [deleteCategory, setDeleteCategory] = useState<{ categoryId: number; name: string } | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		get(`/admin/news/categories`).then((response) => {
			setRows(response);
		});
	}, []);

	const handleCreateCategory = () => {
		post(`/admin/news/category`, { name: categoryName }).then(() => {
			window.location.reload();
		});
	};

	const handleConfirmDelete = async () => {
		if (!deleteCategory) return;
		try {
			setIsDeleting(true);
			await del(`/admin/news/category/${deleteCategory.categoryId}`);
			window.location.reload();
		} catch (error) {
			console.error("Error deleting category:", error);
			setIsDeleting(false);
			setDeleteCategory(null);
		}
	};

	return (
		<>
			<Helmet>
				<title> Categories </title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
					<Button variant="contained" startIcon={<Iconify icon="eva:plus-outline" />} onClick={() => setOpenCreateCategoryDialog(true)}>
						Create Category
					</Button>
				</Stack>

				<Card>
					{rows === null ? (
						<Box sx={{ display: "flex", justifyContent: "center" }} style={{ padding: 20 }}>
							<CircularProgress />
						</Box>
					) : (
						<Scrollbar>
							<TableContainer component={Paper}>
								<Table sx={{ minWidth: 650 }} aria-label="simple table">
									<TableHead>
										<TableRow>
											<TableCell>Category ID</TableCell>
											<TableCell>Category Name</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rows.map((row, index) => (
											<TableRow
												key={index}
												sx={{
													"&:last-child td, &:last-child th": { border: 0 },
												}}
											>
												<TableCell component="th" scope="row">
													{row.categoryId}
												</TableCell>
												<TableCell component="th" scope="row">
													{row.name}
												</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														color="error"
														onClick={() => setDeleteCategory({ categoryId: row.categoryId, name: row.name })}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Scrollbar>
					)}
				</Card>
			</Container>
			<CreateCategoryDialog
				open={openCreateCategoryDialog}
				onClose={() => setOpenCreateCategoryDialog(false)}
				setName={setCategoryName}
				name={categoryName}
				onCreate={handleCreateCategory}
			/>
			<Dialog open={Boolean(deleteCategory)} onClose={() => setDeleteCategory(null)} maxWidth="sm">
				<DialogTitle>Delete Category</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete the category "<strong>{deleteCategory?.name}</strong>"? This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteCategory(null)}>Cancel</Button>
					<Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={isDeleting}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
